import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useContext, useState } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { useIsReadonlyOrLocked } from "@hooks/useIsReadonlyOrLocked";
import {
    CounterpartyCombobox,
    CounterpartyComboboxValue,
} from "../TransactionSplitV2/CounterpartyCombobox";
import { PaginatedTransactionsActionsContext } from "../PaginatedTransactionsTable.context";
import { updateTransactionInCache } from "../useTransactionsQuery";
import styles from "./CustomerCell.module.scss";

function toCachedCustomer(
    selected: CounterpartyComboboxValue | undefined,
    previous: TransactionListItem["customer"],
): TransactionListItem["customer"] {
    if (!selected) {
        return null;
    }
    if (previous?.id === selected.id) {
        return {
            ...previous,
            name: selected.name,
        };
    }
    return {
        id: selected.id,
        name: selected.name,
        shouldBeNormalized: false,
        updatedByUserId: null,
        source: null,
        workspaceId: null,
    };
}

export const CustomerCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, TransactionListItem, unknown>) => {
        const { saveTransaction } = useContext(
            PaginatedTransactionsActionsContext,
        );
        const isReadonlyOrLocked = useIsReadonlyOrLocked(row.original, table);
        // Once a transaction has been billed via a native invoice, the
        // customer it was billed to is locked server-side
        // (CustomerLockedByBillableExpenseError) — disable the cell here so
        // the UI doesn't invite an edit the backend will reject.
        const isInvoiced = row.original.billableExpenseInvoiceId != null;
        const isDisabled = isReadonlyOrLocked || isInvoiced;
        const [isOpen, setIsOpen] = useState(false);

        const handleUpdate = useCallback(
            async (customer?: CounterpartyComboboxValue) => {
                if (isDisabled) {
                    return;
                }
                const customerId = customer?.id ?? null;
                const cachedCustomer = toCachedCustomer(
                    customer,
                    row.original.customer,
                );
                await saveTransaction(
                    {
                        ...row.original,
                        customerId,
                        customer: cachedCustomer,
                    },
                    { customerId },
                );
                updateTransactionInCache(row.original.id, (t) => {
                    let nextCustomer = cachedCustomer;
                    if (customerId == null) {
                        nextCustomer = null;
                    } else if (t.customer?.id === customerId) {
                        nextCustomer = t.customer;
                    }
                    return {
                        ...t,
                        customerId,
                        customer: nextCustomer,
                    };
                });
            },
            [saveTransaction, row.original, isDisabled],
        );

        const handleCreationModalOpenChange = useCallback(
            (creationModalOpen: boolean) => {
                if (creationModalOpen) {
                    setIsOpen(false);
                }
            },
            [],
        );

        const customer = row.original.customer ?? undefined;

        return (
            <ViewTableCell captureClicks className={styles.customerCell}>
                <CounterpartyCombobox
                    selectedCounterparty={customer}
                    onChange={handleUpdate}
                    forTransactionEditing
                    disabled={isDisabled}
                    hideArrow
                    open={isOpen}
                    onOpenChange={setIsOpen}
                    onCreationModalOpenChange={handleCreationModalOpenChange}
                    placeholder="Set billable customer"
                    searchPlaceholder="Search billable customers..."
                    createButtonText="Create new billable customer"
                    emptyStateText="No matching billable customers"
                    clearSelectionText="No billable customer"
                    trigger={
                        <Picker.CellContent
                            readOnly={isDisabled}
                            buttonProps={{
                                tooltip:
                                    isInvoiced && !isReadonlyOrLocked
                                        ? "Billable customer can't be changed once the transaction has been invoiced"
                                        : undefined,
                            }}
                            value={customer?.name}
                        />
                    }
                />
            </ViewTableCell>
        );
    },
);
CustomerCell.displayName = "CustomerCell";
